import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/authAPI';
import { sectionsAPI } from '../services/sectionsAPI';
import { verificationAPI } from '../services/verificationAPI';
import { formatAPIError } from '../utils/validation';
import Header from '../components/layout/Header';
import { useNavigate, Link } from 'react-router-dom';
import './styles/Dashboard.css';
import BackgroundCheckDashboard from '../components/backgroundcheck/BackgroundCheckDashboard';

const Dashboard = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showMFASettings, setShowMFASettings] = useState(false);

  // Profile data state
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Education state
  const [educations, setEducations] = useState([]);
  const [loadingEducation, setLoadingEducation] = useState(true);
  const [educationError, setEducationError] = useState('');

  // Certification state
  const [certifications, setCertifications] = useState([]);
  const [loadingCertification, setLoadingCertification] = useState(true);
  const [certificationError, setCertificationError] = useState('');

  // Employment state
  const [employments, setEmployments] = useState([]);
  const [loadingEmployment, setLoadingEmployment] = useState(true);
  const [employmentError, setEmploymentError] = useState('');

  // Advanced Dashboard State
  const [dashboardStats, setDashboardStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);

  // MFA Settings State
  const [mfaSettings, setMfaSettings] = useState({
    mfaEnabled: user?.mfaEnabled || false,
    mfaType: user?.mfaType || 'email'
  });
  const [tempSettings, setTempSettings] = useState({ ...mfaSettings });

  // Load Advanced Dashboard Data
  useEffect(() => {
    const loadAdvancedDashboardData = async () => {
      try {
        console.log('📊 Loading advanced dashboard data...');

        // Load dashboard statistics
        const dashboardResponse = await verificationAPI.getDashboard();
        setDashboardStats(dashboardResponse);
        setRecentActivity(dashboardResponse.recentActivity || []);

        console.log('✅ Advanced dashboard data loaded:', dashboardResponse);
      } catch (error) {
        console.error('❌ Failed to load advanced dashboard data:', error);
      } finally {
      }
    };

    loadAdvancedDashboardData();
  }, []);

  // Load fresh profile data on component mount
  const loadDashboardData = useCallback(async () => {
  try {
    setLoadingProfile(true);
    console.log('🏠 Loading dashboard profile data...');

    const response = await authAPI.getProfile();
    console.log('📊 Dashboard profile data:', response.data);
    setProfileData(response.data);

    if (response.data) {
      const updatedUserData = {
        ...user,
        firstName: response.data.firstName || user?.firstName,
        lastName: response.data.lastName || user?.lastName,
        email: response.data.account?.email || user?.email,
        phone: response.data.account?.phone || user?.phone,
      };
      console.log('🔄 Updating AuthContext with fresh profile data');
      updateUser(updatedUserData);
    }
  } catch (error) {
    console.error('❌ Failed to load dashboard profile:', error);
    setProfileData(null);
  } finally {
    setLoadingProfile(false);
  }
}, [user, updateUser]);

useEffect(() => {
  loadDashboardData();
}, [loadDashboardData]);


  // Load education data
  useEffect(() => {
    const loadEducationData = async () => {
      try {
        setLoadingEducation(true);
        setEducationError('');
        console.log('📚 Loading education data...');
        const response = await sectionsAPI.education.getAll();
        setEducations(response.data || []);
        console.log('✅ Education data loaded:', response.data?.length || 0, 'entries');
      } catch (error) {
        console.error('❌ Failed to load education data:', error);
        setEducationError('Failed to load education data');
        setEducations([]);
      } finally {
        setLoadingEducation(false);
      }
    };

    loadEducationData();
  }, []);

  useEffect(() => {
    const loadCertificationData = async () => {
      try {
        setLoadingCertification(true);
        setCertificationError(''); // Clear previous errors
        const response = await sectionsAPI.certification.getAll();
        setCertifications(response.data || []);
      } catch (error) {
        console.error('Failed to load certification data:', error);
        setCertificationError('Failed to load certification data');
        setCertifications([]);
      } finally {
        setLoadingCertification(false);
      }
    };

    loadCertificationData();
  }, []);

  useEffect(() => {
    const loadEmploymentData = async () => {
      try {
        setLoadingEmployment(true);
        setEmploymentError('');
        const response = await sectionsAPI.employment.getAll();
        setEmployments(response.data || []);
      } catch (error) {
        setEmploymentError('Failed to load employment data');
        setEmployments([]);
      } finally {
        setLoadingEmployment(false);
      }
    };
    loadEmploymentData();
  }, []);

  useEffect(() => {
    // Update settings when user data changes
    if (user) {
      setMfaSettings({
        mfaEnabled: user.mfaEnabled || false,
        mfaType: user.mfaType || 'email'
      });
      setTempSettings({
        mfaEnabled: user.mfaEnabled || false,
        mfaType: user.mfaType || 'email'
      });
    }
  }, [user]);

  const handleMFAToggle = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const newMfaEnabled = !mfaSettings.mfaEnabled;
      await authAPI.updateMFA({
  mfaEnabled: newMfaEnabled,
  mfaType: tempSettings.mfaType
});

      setMfaSettings({
        mfaEnabled: newMfaEnabled,
        mfaType: tempSettings.mfaType
      });

      setSuccess(newMfaEnabled
        ? `🎉 Two-Factor Authentication enabled via ${tempSettings.mfaType}!`
        : '✅ Two-Factor Authentication disabled successfully!'
      );
      setTimeout(() => setSuccess(''), 5000);

    } catch (error) {
      console.error('❌ MFA update failed:', error);
      setError(formatAPIError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleMfaTypeChange = (newType) => {
    setTempSettings(prev => ({ ...prev, mfaType: newType }));
  };


  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Helper function to get display name
  const getDisplayName = () => {
    if (loadingProfile) return 'Loading...';
    const firstName = profileData?.firstName || user?.firstName;
    const lastName = profileData?.lastName || user?.lastName;
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    return 'Not provided';
  };

  // Helper function to get display email
  const getDisplayEmail = () => {
    return profileData?.account?.email || user?.email || 'Not provided';
  };

  // Helper function to get display phone
  const getDisplayPhone = () => {
    return profileData?.account?.phone || user?.phone || 'Not provided';
  };

  // Helper function to format education date
  const formatEducationDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  };

  // Helper function to get education date range
  const getEducationDateRange = (education) => {
    const startDate = formatEducationDate(education.startDate);
    if (education.isOngoing) {
      return `${startDate} - Present`;
    }
    const endDate = formatEducationDate(education.endDate);
    return `${startDate} - ${endDate}`;
  };

  const formatEmploymentDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };
  const getEmploymentDateRange = (employment) => {
    const start = formatEmploymentDate(employment.startDate);
    return employment.isOngoing ? `${start} - Present` : `${start} - ${formatEmploymentDate(employment.endDate)}`;
  };

  const getVerificationBadge = (status) => {
    switch (status) {
      case 'VALIDATED':
        return <span className="verification-badge validated">✓ Verified</span>;
      case 'PENDING':
        return <span className="verification-badge pending">⏳ Pending</span>;
      case 'REJECTED':
        return <span className="verification-badge rejected">✗ Rejected</span>;
      default:
        return <span className="verification-badge not-requested">Not Requested</span>;
    }
  };

  // Enhanced Stats Calculation
  const getVerificationStats = () => {
    if (!dashboardStats) {
      // Fallback calculation from existing data
      const totalEducation = educations.length;
      const totalCertification = certifications.length;
      const totalEmployment = employments.length;
      const verifiedEducation = educations.filter(e => e.verification?.status === 'VALIDATED').length;
      const verifiedCertification = certifications.filter(c => c.verification?.status === 'VALIDATED').length;
      const verifiedEmployment = employments.filter(e => e.verification?.status === 'VALIDATED').length;
      const pendingEmployment = employments.filter(e => e.verification?.status === 'PENDING').length;
      const rejectedEmployment = employments.filter(e => e.verification?.status === 'REJECTED').length;

      return {
        total: totalEducation + totalCertification + totalEmployment,
        verified: verifiedEducation + verifiedCertification + verifiedEmployment,
        pending: educations.filter(e => e.verification?.status === 'PENDING').length +
          certifications.filter(c => c.verification?.status === 'PENDING').length +
          pendingEmployment,
        rejected: educations.filter(e => e.verification?.status === 'REJECTED').length +
          certifications.filter(c => c.verification?.status === 'REJECTED').length +
          rejectedEmployment,
        completionPercentage: (totalEducation + totalCertification + totalEmployment) > 0
          ? Math.round(((verifiedEducation + verifiedCertification + verifiedEmployment) /
            (totalEducation + totalCertification + totalEmployment)) * 100)
          : 0
      };

    }

    // Use backend stats if available
    return {
      total: (dashboardStats.summary?.education?.total || 0)
        + (dashboardStats.summary?.certifications?.total || 0)
        + (dashboardStats.summary?.employment?.total || 0),
      verified: (dashboardStats.summary?.education?.verified || 0)
        + (dashboardStats.summary?.certifications?.verified || 0)
        + (dashboardStats.summary?.employment?.verified || 0),
      pending: dashboardStats.verificationStats?.pending || 0,
      rejected: dashboardStats.verificationStats?.rejected || 0,
      completionPercentage: dashboardStats.summary?.completionPercentage || 0
    };

  };

  // Format recent activity
  const formatRecentActivity = (activity) => {
    const date = new Date(activity.timestamp || activity.createdAt);
    const timeAgo = Math.round((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
    return timeAgo === 0 ? 'Today' : `${timeAgo} days ago`;
  };

  const stats = getVerificationStats();

  return (
    <>
      <Header />
      <div style={{
        padding: '12px 20px',
        background: '#c7beefff',
        borderBottom: '1px solid #ddd',
        textAlign: 'center',
        fontSize: '14px',
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px'
      }}>
        <span style={{ color: '#666', fontWeight: 'normal' }}>•</span>
        <span>ADMIN Dashboard</span>
        <span style={{ color: '#666', fontWeight: 'normal' }}>•</span>
      </div>

      <div className="dashboard-container" style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>

        {/* Welcome Section */}
        <div className="dashboard-header" style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Welcome back, {getDisplayName()}!</h1>
          <p style={{ color: '#666', fontSize: '1rem' }}>Manage your profile and track your verification progress</p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
            {success}
          </div>
        )}
        {error && (
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {/* Enhanced Stats Overview */}
        <div className="dashboard-overview">
          <div className="stats-grid">
            <div className="stat-card total">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <h3>{stats.total}</h3>
                <p>Total Records</p>
                <small>{educations.length} education, {certifications.length} certifications</small>
              </div>
            </div>

            <div className="stat-card verified">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <h3>{stats.verified}</h3>
                <p>Verified Records</p>
                <small>{stats.total > 0 ? `${Math.round((stats.verified / stats.total) * 100)}%` : '0%'} of total</small>
              </div>
            </div>

            <div className="stat-card pending">
              <div className="stat-icon">⏳</div>
              <div className="stat-content">
                <h3>{stats.pending}</h3>
                <p>Pending Verification</p>
                <small>Awaiting review</small>
              </div>
            </div>

            <div className="stat-card completion">
              <div className="stat-icon">🎯</div>
              <div className="stat-content">
                <h3>{stats.completionPercentage}%</h3>
                <p>Profile Complete</p>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${stats.completionPercentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Dashboard Content */}
        <div className="dashboard-content">

          {/* Main Content Area */}
          <div className="dashboard-main">

            {/* Education Section */}
            <div className="dashboard-card">
              <div className="card-header">
                <h2>📚 Education</h2>
                <Link to="/sections" className='card-header-btn' style={{
                  padding: '0.5rem 1rem',

                  color: 'white',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  + Add Education
                </Link>
              </div>

              {loadingEducation ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading education data...</p>
                </div>
              ) : educationError ? (
                <div className="error-state">
                  <p>{educationError}</p>
                  <p>Unable to load education data</p>
                </div>
              ) : educations.length > 0 ? (
                <div className="education-list">
                  {educations.slice(0, 3).map(education => (
                    <div key={education.id} className="education-item">
                      <div className="education-info">
                        <h4>{education.degree || 'Degree not specified'}</h4>
                        <p className="education-institution">{education.institution}</p>
                        <small className="education-dates">{getEducationDateRange(education)}</small>
                      </div>
                      {getVerificationBadge(education.verification?.status)}
                    </div>
                  ))}
                  {educations.length > 3 && (
                    <div className="show-more">
                      <Link to="/sections" className="btn-link">
                        View all {educations.length} education records →
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No education added yet</p>
                  <Link to="/sections" className="btn-link">Add Education</Link>
                </div>
              )}
            </div>

            {/* Certification Section */}
            <div className="dashboard-card">
              <div className="card-header">
                <h2>🏆 Certifications</h2>
                <Link to="/sections" className='card-header-btn' style={{
                  padding: '0.5rem 1rem',
                  color: 'white',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  + Add Certification
                </Link>
              </div>

              {loadingCertification ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading certifications...</p>
                </div>
              ) : certificationError ? (
                <div className="error-state">
                  <p>{certificationError}</p>
                  <p>Unable to load certification data</p>
                </div>
              ) : certifications.length > 0 ? (
                <div className="certification-list">
                  {certifications.slice(0, 3).map(certification => (
                    <div key={certification.id} className="certification-item">
                      <div className="certification-info">
                        <h4>{certification.name || 'Certification not specified'}</h4>
                        <p className="certification-issuer">{certification.issuer || 'No issuer specified'}</p>
                        <small className="certification-date">
                          {certification.isPursuing ? 'Currently Pursuing' :
                            certification.issuedAt ? `Issued ${formatEducationDate(certification.issuedAt)}` :
                              'Date not specified'}
                        </small>
                      </div>
                      {getVerificationBadge(certification.verification?.status)}
                    </div>
                  ))}
                  {certifications.length > 3 && (
                    <div className="show-more">
                      <Link to="/sections" className="btn-link">
                        View all {certifications.length} certifications →
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No certifications added yet</p>
                  <Link to="/sections" className="btn-link">Add Certification</Link>
                </div>
              )}
            </div>

            {/* Employment Section */}
            <div className="dashboard-card">
              <div className="card-header">
                <h2>💼 Employment</h2>
                <Link to="/sections" className='card-header-btn' style={{
                  padding: '0.5rem 1rem',
                  color: 'white',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  + Add Employment
                </Link>
              </div>
              {loadingEmployment ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading employment data...</p>
                </div>
              ) : employmentError ? (
                <div className="error-state">
                  <p className="error-message">{employmentError}</p>
                  <small>Unable to load employment data</small>
                </div>
              ) : employments.length > 0 ? (
                <div className="employment-list">
                  {employments.slice(0, 3).map((employment, index) => (
                    <div key={index} className="employment-item">
                      <div className="employment-info">
                        <h4>{employment.jobTitle || 'Employment not specified'}</h4>
                        <p className="employment-company">{employment.company || 'No company specified'}</p>
                        <small className="employment-dates">
                          {getEmploymentDateRange(employment)}
                        </small>
                      </div>
                      {getVerificationBadge(employment.verification?.status)}

                    </div>
                  ))}
                  {employments.length > 3 && (
                    <p className="show-more">
                      <Link to="/sections">View all {employments.length} employment records</Link>
                    </p>
                  )}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No employment history added yet</p>
                  <Link to="/sections" className="btn-primary">Add Employment</Link>
                </div>
              )}
            </div>

            {/* Background Checks Section */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>🕵️ Background Checks</h3>
                <Link to="#" className="view-all-link">
                  Manage Background Checks →
                </Link>
              </div>
              <div className="card-content">
                <BackgroundCheckDashboard />
              </div>
            </div>

          </div>

          {/* Sidebar */}
          <div className="dashboard-sidebar">

            {/* Profile Info Card */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>👤 Profile Info</h3>
                {/*Edit Profile Button */}
                <Link
                  to="/candidate/profile"
                  style={{
                    fontSize: '0.875rem',
                    color: '#3b82f6',
                    textDecoration: 'none',
                    fontWeight: '500'
                  }}
                >
                  Edit Profile
                </Link>
              </div>
              <div className="profile-info">
                <div className="info-row">
                  <span className="info-label">Name:</span>
                  <span className="info-value">{getDisplayName()}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Email:</span>
                  <span className="info-value">{getDisplayEmail()}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Phone:</span>
                  <span className="info-value">{getDisplayPhone()}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Role:</span>
                  <span className="verification-badge validated">ADMIN</span>
                </div>
              </div>
            </div>


            {/*  MFA Settings Card */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>🔒 Security Settings</h3>
              </div>
              <div className="mfa-settings">
                <div className="mfa-status">
                  <div className="mfa-icon">
                    {mfaSettings.mfaEnabled ? '🔒' : '🔓'}
                  </div>
                  <div className="mfa-info">
                    <h4>Two-Factor Authentication</h4>
                    <p style={{ fontSize: '0.875rem', color: '#666', margin: '0.25rem 0 0 0' }}>
                      {mfaSettings.mfaEnabled
                        ? `Enabled via ${mfaSettings.mfaType}`
                        : 'Add an extra layer of security to your account'}
                    </p>
                  </div>
                </div>
                <button
                  className="btn-primary"
                  onClick={() => setShowMFASettings(true)}
                  style={{
                    width: '100%',
                    marginTop: '1rem',
                    padding: '0.75rem',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  {mfaSettings.mfaEnabled ? 'Manage MFA' : 'Enable MFA'}
                </button>
              </div>
            </div>


            {/* Quick Actions Card */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>⚡ Quick Actions</h3>
              </div>
              <div className="action-buttons">
                <Link to="/candidate/profile" className="action-btn">
                  <span className="action-icon">👤</span>
                  <span>Edit Profile</span>
                </Link>
                <Link to="/candidate/sections" className="action-btn">
                  <span className="action-icon">📚</span>
                  <span>Add Record</span>
                </Link>
                <Link to="/candidate/background-check" className="action-btn">
                  <span className="action-icon">🔍</span>
                  <span>Background Check</span>
                </Link>
                <Link to="/change-password" className="action-btn">
                  <span className="action-icon">🔒</span>
                  <span>Change Password</span>
                </Link>
                <button onClick={handleLogout} className="action-btn logout-btn">
                  <span className="action-icon">🚪</span>
                  <span>Logout</span>
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            {recentActivity.length > 0 && (
              <div className="dashboard-card recent-activity">
                <h3>📋 Recent Activity</h3>
                <div className="activity-list">
                  {recentActivity.slice(0, 5).map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className="activity-icon">
                        {activity.type === 'EDUCATION' ? '🎓' :
                          activity.type === 'EMPLOYMENT' ? '💼' :
                            activity.type === 'CERTIFICATION' ? '🏆' : '📋'}
                      </div>
                      <div className="activity-content">
                        <p className="activity-action">
                          <strong>{activity.action || 'Updated'}</strong>
                        </p>
                        <p className="activity-item-name">{activity.itemName}</p>
                        <small className="activity-time">{formatRecentActivity(activity)}</small>
                      </div>
                      <div className={`activity-status ${(activity.status || '').toLowerCase()}`}>
                        {activity.status === 'VALIDATED' ? '✅' :
                          activity.status === 'PENDING' ? '⏳' :
                            activity.status === 'REJECTED' ? '❌' : '📝'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Verification Progress Card */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>📊 Verification Progress</h3>
              </div>
              <div className="verification-progress">
                <div className="progress-stats">
                  <div className="progress-item">
                    <span>Verified</span>
                    <span className="progress-count verified">{stats.verified}</span>
                  </div>
                  <div className="progress-item">
                    <span>Pending</span>
                    <span className="progress-count pending">{stats.pending}</span>
                  </div>
                  <div className="progress-item">
                    <span>Total</span>
                    <span className="progress-count">{stats.total}</span>
                  </div>
                </div>
                <div className="overall-progress">
                  <div className="progress-bar-container">
                    <div className="progress-bar-fill" style={{ width: `${stats.completionPercentage}%` }}></div>
                  </div>
                  <p className="progress-percentage">{stats.completionPercentage}% Complete</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* MFA Settings Modal */}
      {/* Simpler MFA Settings Modal*/}
      {showMFASettings && (
        <div className="dashboard-modal-overlay" onClick={() => setShowMFASettings(false)}>
          <div className="dashboard-modal-content mfa-simple-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dashboard-modal-header">
              <h2>Configure Two-Factor Authentication</h2>
              <button className="dashboard-close-btn" onClick={() => setShowMFASettings(false)}>×</button>
            </div>

            <div className="dashboard-modal-body">
              {/* MFA Type Selection */}
              <div className="mfa-type-options-simple">
                <label className={`mfa-type-card ${tempSettings.mfaType === 'email' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="mfaType"
                    value="email"
                    checked={tempSettings.mfaType === 'email'}
                    onChange={() => handleMfaTypeChange('email')}
                  />
                  <div className="card-content">
                    <div className="card-icon">📧</div>
                    <div className="card-text">
                      <h4>Email Verification</h4>
                      <p>Send verification codes to your email</p>
                    </div>
                  </div>
                </label>

                <label className={`mfa-type-card ${tempSettings.mfaType === 'sms' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="mfaType"
                    value="sms"
                    checked={tempSettings.mfaType === 'sms'}
                    onChange={() => handleMfaTypeChange('sms')}
                  />
                  <div className="card-content">
                    <div className="card-icon">📱</div>
                    <div className="card-text">
                      <h4>SMS Verification</h4>
                      <p>Send verification codes to your phone</p>
                    </div>
                  </div>
                </label>
              </div>

              {/* Enable MFA Button */}
              <button
                className="btn-enable-mfa"
                onClick={handleMFAToggle}
                disabled={loading}
              >
                <span className="btn-icon">🔒</span>
                {loading ? 'Processing...' : mfaSettings.mfaEnabled ? 'Update MFA' : 'Enable MFA'}
              </button>

              {/* Optional: Disable MFA Button */}
              {mfaSettings.mfaEnabled && (
                <button
                  className="btn-disable-mfa"
                  onClick={handleMFAToggle}
                  disabled={loading}
                >
                  Disable MFA
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default Dashboard;
